import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { get_payment_instructions } from '@/api/cabinet'
import type { PaymentInstructions } from '@/types/subscription'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Bank details the cabinet must use to pay by wire transfer. */
export function WireInstructionsDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation()
  const [instructions, setInstructions] = useState<PaymentInstructions | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!open) return
    setFailed(false)
    get_payment_instructions().then(setInstructions).catch(() => setFailed(true))
  }, [open])

  const rows: [string, string, string | undefined][] = instructions
    ? [
        ['wire_beneficiary', 'Beneficiary', instructions.legal_name],
        ['wire_bank', 'Bank', instructions.bank_name],
        ['wire_rib', 'RIB', instructions.rib],
        ['wire_iban', 'IBAN', instructions.iban],
        ['wire_swift', 'SWIFT', instructions.swift],
      ]
    : []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('payment_pending') || 'Payment pending'}</DialogTitle>
        </DialogHeader>
        <p className='text-sm text-muted-foreground'>
          {t('wire_instructions_desc') ||
            'Your subscription is activated once we receive and validate your bank transfer. Use the details below.'}
        </p>
        {failed ? (
          <p className='text-sm text-destructive'>{t('wire_instructions_error') || 'Unable to load bank details.'}</p>
        ) : (
          <dl className='mt-3 space-y-1 text-sm'>
            {rows.filter(([, , v]) => v).map(([key, label, value]) => (
              <div key={key} className='flex justify-between gap-4'>
                <dt className='text-muted-foreground'>{t(key) || label}</dt>
                <dd className='font-mono'>{value}</dd>
              </div>
            ))}
          </dl>
        )}
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>{t('close') || 'Close'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
